import { computed, onMounted, ref } from "vue";
import { toBoolean } from "#ce/utils/props";

/** @typedef {import("../../SpDialog/props.js").SpDialogProps} SpDialogProps */

const CLOSE_BUTTON_SIZE_PROPERTY = "--sp-comp-modal-header-close-button-size";

/**
 * Close-button concerns for sp-modal: whether to render it and its size.
 *
 * Visibility is tri-state on `closeable`: an explicit value wins, and when
 * `closeable` is unset the button shows unless the modal is `persistent`. Size
 * is read once on mount from the `--sp-comp-modal-header-close-button-size`
 * custom property so a theme can override the default.
 *
 * @param {SpDialogProps & { closeable?: Boolean|String }} props - The sp-modal props.
 * @returns {{
 *   closeButtonSize: import("vue").Ref<string>,
 *   showCloseButton: import("vue").ComputedRef<boolean>,
 * }}
 */
export function useModalCloseButton(props) {
  const closeButtonSize = ref("small");

  function readCloseButtonSize() {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(CLOSE_BUTTON_SIZE_PROPERTY);
    if (value && value !== closeButtonSize.value) {
      closeButtonSize.value = value;
    }
  }

  const showCloseButton = computed(() => {
    if (props.closeable === undefined) {
      return !toBoolean(props.persistent);
    }
    return toBoolean(props.closeable);
  });

  onMounted(readCloseButtonSize);

  return { closeButtonSize, showCloseButton };
}
